import React, { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Tooltip,
  IconButton,
  Button,
  InputAdornment,
  Grid,
  Select,
  MenuItem,
  TextField,
  Collapse
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useGlobalSelector from '../../../store/Slice/Global/GlobalSelector';

// ----------------------------------------------------------------------

export default function UserListToolbar({
  numSelected,
  filterName,
  onFilterName,
  parent = 'users',
  filterState,
  setFilterState,
  setFilterDatesRange,
  setFilterName,
  typeIdValue,
  handleExport
}) {
  const [isOpenFilter, setOpenFilter] = useState(false);
  const { AccessLevelList, DepartmentListDrop } = useGlobalSelector();
  const navigate = useNavigate();

  const handleRemoveFilter = () => {
    if (typeof setFilterDatesRange === 'function') {
      setFilterDatesRange([
        {
          startDate: null,
          endDate: null,
          key: 'selection'
        }
      ]);
    }

    if (typeof setFilterState === 'function') {
      setFilterState({
        ...filterState,
        product: '',
        productId: null,
        city: null,
        cityId: null,
        isActive: '',
        employmentType: '',
        statusId: null,
        departmentId: null,
        branchId: null,
        accessLevelId: null
      });
    }

    if (typeof setFilterName === 'function') {
      setFilterName('');
    }
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#ffffff' }}>
      {/* Top Controls Bar */}
      <Box
        sx={{
          minHeight: 64,
          px: { xs: 2, sm: 3 },
          py: 1.5,
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5
        }}
      >
        {/* Left Action Buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {typeIdValue === 304 && (
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              onClick={() => navigate(`/${parent}/add`)}
              sx={{
                bgcolor: '#0284c7',
                color: '#ffffff',
                borderRadius: '10px',
                px: 2.2,
                py: 0.9,
                fontSize: 13.5,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 14px rgba(2, 132, 199, 0.25)',
                '&:hover': { bgcolor: '#0369a1' }
              }}
            >
              Add New User
            </Button>
          )}

          <Button
            variant="outlined"
            startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={() => handleExport(typeIdValue)}
            sx={{
              borderColor: '#e2e8f0',
              color: '#475569',
              borderRadius: '10px',
              px: 2,
              py: 0.9,
              fontSize: 13.5,
              fontWeight: 600,
              textTransform: 'none',
              bgcolor: '#ffffff',
              '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }
            }}
          >
            Export Data
          </Button>
        </Box>

        {/* Right Search & Filter Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TextField
            size="small"
            value={filterName}
            onChange={onFilterName}
            placeholder="Search Users..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                </InputAdornment>
              )
            }}
            sx={{
              width: { xs: '100%', sm: 260, md: 280 },
              '& .MuiOutlinedInput-root': {
                bgcolor: '#ffffff',
                borderRadius: '10px',
                fontSize: '13px',
                '& fieldset': {
                  borderColor: '#e2e8f0'
                },
                '&:hover fieldset': {
                  borderColor: '#cbd5e1'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0284c7'
                }
              }
            }}
          />

          <Button
            variant="outlined"
            onClick={() => setOpenFilter((prev) => !prev)}
            startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              color: isOpenFilter ? '#0284c7' : '#475569',
              borderColor: isOpenFilter ? '#0284c7' : '#e2e8f0',
              bgcolor: isOpenFilter ? 'rgba(2, 132, 199, 0.06)' : '#ffffff',
              height: 40,
              px: 2,
              '&:hover': {
                borderColor: '#0284c7',
                bgcolor: 'rgba(2, 132, 199, 0.08)'
              }
            }}
          >
            Filters
          </Button>
        </Box>
      </Box>

      {/* Collapsible Filter Panel */}
      <Collapse in={isOpenFilter}>
        <Box
          sx={{
            p: 2.5,
            bgcolor: '#f8fafc',
            borderBottom: '1px solid #e2e8f0'
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Department Select */}
            <Grid item xs={12} sm={4} md={3.5}>
              <Select
                fullWidth
                size="small"
                value={filterState?.departmentId ?? ' '}
                onChange={(e) =>
                  setFilterState &&
                  setFilterState({
                    ...filterState,
                    departmentId: e.target.value === ' ' ? null : e.target.value
                  })
                }
                sx={{
                  bgcolor: '#ffffff',
                  borderRadius: '8px',
                  fontSize: 13,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                }}
              >
                <MenuItem value=" " sx={{ fontSize: 13 }}>
                  Select Department
                </MenuItem>
                {Array.isArray(DepartmentListDrop)
                  ? DepartmentListDrop.map((opt) => (
                      <MenuItem
                        key={opt?.value ?? opt?.id}
                        value={opt?.value ?? opt?.id}
                        sx={{ fontSize: 13 }}
                      >
                        {opt?.label ?? opt?.name ?? opt?.value}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </Grid>

            {/* Access Level */}
            <Grid item xs={12} sm={4} md={3.5}>
              <Select
                fullWidth
                size="small"
                value={filterState?.accessLevelId ?? ' '}
                onChange={(e) =>
                  setFilterState &&
                  setFilterState({
                    ...filterState,
                    accessLevelId: e.target.value === ' ' ? null : e.target.value
                  })
                }
                sx={{
                  bgcolor: '#ffffff',
                  borderRadius: '8px',
                  fontSize: 13,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                }}
              >
                <MenuItem value=" " sx={{ fontSize: 13 }}>
                  Select Access Level
                </MenuItem>
                {Array.isArray(AccessLevelList)
                  ? AccessLevelList.map((opt) => (
                      <MenuItem
                        key={opt?.accessLevelId ?? opt?.id}
                        value={opt?.accessLevelId ?? opt?.id}
                        sx={{ fontSize: 13 }}
                      >
                        {opt?.accessLevelName ?? opt?.name ?? opt?.label ?? opt?.value}
                      </MenuItem>
                    ))
                  : null}
              </Select>
            </Grid>

            {/* Status Select */}
            <Grid item xs={12} sm={3} md={3.5}>
              <Select
                fullWidth
                size="small"
                displayEmpty
                value={filterState?.isActive ?? ''}
                onChange={(e) =>
                  setFilterState &&
                  setFilterState({
                    ...filterState,
                    isActive: e.target.value === '' ? null : e.target.value
                  })
                }
                renderValue={(value) => {
                  if (value === '' || value === null) {
                    return <span style={{ color: '#94a3b8' }}>Select Status</span>;
                  }
                  return value === 'true' ? 'Active' : 'Inactive';
                }}
                sx={{
                  bgcolor: '#ffffff',
                  borderRadius: '8px',
                  fontSize: 13,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                }}
              >
                <MenuItem value="" disabled sx={{ fontSize: 13 }}>
                  Select Status
                </MenuItem>
                <MenuItem value="true" sx={{ fontSize: 13 }}>
                  Active
                </MenuItem>
                <MenuItem value="false" sx={{ fontSize: 13 }}>
                  Inactive
                </MenuItem>
              </Select>
            </Grid>

            {/* Reset Filter Button */}
            <Grid item xs={12} sm={1} md="auto">
              <Tooltip title="Reset Filter" arrow>
                <IconButton
                  onClick={handleRemoveFilter}
                  size="small"
                  sx={{
                    bgcolor: '#fee2e2',
                    color: '#dc2626',
                    borderRadius: '8px',
                    p: 1,
                    '&:hover': { bgcolor: '#fecaca' }
                  }}
                >
                  <FilterListOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
}
